import { useState } from "react";
import { ExternalLink, Rocket } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

const CATS = ["community", "energy", "education", "empowerment", "water", "food", "health", "agriculture", "housing", "emergency"];

export const ConvertToProject = ({ application, onConverted }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", category: CATS.includes(application.primary_category) ? application.primary_category : "community",
    target_amount_numeric: application.estimated_value || application.amount_requested || application.total_project_cost || 0,
    location: [application.community_name || application.community, application.lga, application.state].filter(Boolean).join(", "),
    beneficiaries_estimate: application.beneficiaries_count || application.proposed_beneficiaries_count || 0, image_url: "",
  });

  if (application.linked_project_serial) {
    return (
      <div className="p-4 rounded-md border border-green-200 bg-green-50 flex items-center justify-between gap-3" data-testid="app-linked-project">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-green-800 font-semibold">Linked public project</div>
          <div className="font-mono text-sm font-bold text-[#0d1724]" data-testid="app-linked-project-serial">{application.linked_project_serial}</div>
        </div>
        <a href={`/projects/${application.linked_project_serial}/trace`} target="_blank" rel="noreferrer" className="dk-btn-primary !py-2 text-xs" data-testid="app-linked-project-trace">
          View trace <ExternalLink size={12} />
        </a>
      </div>
    );
  }

  const convert = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/applications/${application.id}/convert-to-project`, {
        ...form, target_amount_numeric: Number(form.target_amount_numeric || 0), beneficiaries_estimate: Number(form.beneficiaries_estimate || 0),
      });
      toast.success(`Project ${data.serial} is live for sponsorship`);
      onConverted(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const cls = "px-3 py-2 border border-black/15 rounded-md text-sm bg-white w-full";

  return (
    <div className="p-4 rounded-md border border-[#0d1724]/15 bg-white" data-testid="app-convert-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-serif text-lg font-bold">Sponsor-ready project</h3>
          <p className="text-xs text-[#0a0d12]/60">Publish this request as a public project with a DKF-PRJ serial and full trace.</p>
        </div>
        {!open && (
          <button onClick={() => setOpen(true)} className="dk-btn-primary !py-2 text-sm whitespace-nowrap" data-testid="app-convert-btn">
            <Rocket size={14} /> Convert to project
          </button>
        )}
      </div>
      {open && (
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <input value={form.title} onChange={set("title")} placeholder="Project title (optional)" className={`${cls} sm:col-span-2`} data-testid="convert-title" />
          <select value={form.category} onChange={set("category")} className={cls} data-testid="convert-category">
            {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="number" min="0" value={form.target_amount_numeric} onChange={set("target_amount_numeric")} placeholder="Target amount (₦)" className={cls} data-testid="convert-target" />
          <input value={form.location} onChange={set("location")} placeholder="Location (Community, LGA, State)" className={cls} data-testid="convert-location" />
          <input type="number" min="0" value={form.beneficiaries_estimate} onChange={set("beneficiaries_estimate")} placeholder="Beneficiaries estimate" className={cls} data-testid="convert-beneficiaries" />
          <input value={form.image_url} onChange={set("image_url")} placeholder="Image URL (optional)" className={`${cls} sm:col-span-2`} data-testid="convert-image" />
          <div className="sm:col-span-2 flex gap-2">
            <button onClick={convert} disabled={busy} className="dk-btn-primary !py-2 text-sm disabled:opacity-60" data-testid="app-convert-submit">
              {busy ? "Creating…" : "Create public project"}
            </button>
            <button onClick={() => setOpen(false)} className="text-sm px-3 text-[#0a0d12]/60" data-testid="app-convert-cancel">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};
